import { useEffect, useRef, useState } from "react";
import AppsIcon from "@mui/icons-material/Apps";
import BuildIcon from "@mui/icons-material/Build";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SaveIcon from "@mui/icons-material/Save";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { formatLocation } from "../utils/locations";

const ROWS = [...Array.from({ length: 20 }, (_, index) => index + 1), 99];
const STANDARD_BAYS = Array.from({ length: 12 }, (_, index) => index + 1);
const ROW_99_BAYS = Array.from({ length: 30 }, (_, index) => index + 1);

export function LocationPicker({
  activeItem,
  selectedRow,
  selectedBay,
  isHardline,
  initialTab = "row",
  rowButtonsPerRow,
  bayButtonsPerRow,
  saveDisabled,
  onSelectRow,
  onSelectBay,
  onHardlineChange,
  onSave,
}) {
  const [tab, setTab] = useState(initialTab);
  const savePanelRef = useRef(null);
  const saveButtonRef = useRef(null);

  useEffect(() => {
    if (!selectedRow) {
      setTab("row");
      return;
    }
    if (initialTab === "bay") {
      setTab("bay");
    }
  }, [initialTab, selectedRow]);

  useEffect(() => {
    if (!saveDisabled) {
      window.setTimeout(() => {
        saveButtonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        saveButtonRef.current?.focus();
      }, 80);
    }
  }, [saveDisabled, selectedBay]);

  const scrollSavePanelIntoView = () => {
    window.setTimeout(() => {
      savePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  const selectedLocationText =
    selectedRow && selectedBay
      ? formatLocation({
          row_number: selectedRow,
          bay_number: selectedBay,
          is_hardline: isHardline,
        })
      : selectedRow
        ? `${isHardline ? `Hardline row H${String(selectedRow).padStart(2, "0")}` : `Row ${selectedRow}`} - Bay not selected`
        : "No row or bay selected";
  const bays = Number(selectedRow) === 99 ? ROW_99_BAYS : STANDARD_BAYS;

  return (
    <section className="panel picker-panel" aria-label="Row and bay picker">
      <div className="picker-top">
        <div className="tabs" role="tablist" aria-label="Location picker sections">
          <button
            type="button"
            className={`touch-button tab-button ${tab === "row" ? "active" : ""}`}
            onClick={() => setTab("row")}
          >
            <ViewModuleIcon /> ROW
          </button>
          <button
            type="button"
            className={`touch-button tab-button ${tab === "bay" ? "active" : ""}`}
            onClick={() => setTab("bay")}
            disabled={!selectedRow}
          >
            <AppsIcon /> BAY
          </button>
        </div>
        <button
          type="button"
          className={`touch-button hardline-toggle ${isHardline ? "active" : ""}`}
          aria-pressed={isHardline}
          onClick={() => onHardlineChange(!isHardline)}
        >
          <BuildIcon /> HARDLINE
        </button>
      </div>

      {tab === "row" && (
        <section
          className="panel selector-panel row-panel"
          aria-label={`Select row for ${activeItem?.item_name || "item"}`}
        >
          <h3 className="panel-header">Select Row for {activeItem?.item_name || "Item"}</h3>
          <div className="row-selector-shell">
            <div
              className="grid-buttons"
              style={{ "--buttons-per-row": Number(rowButtonsPerRow) }}
            >
              {ROWS.map((row) => (
                <button
                  key={row}
                  type="button"
                  className={`grid-button ${row === Number(selectedRow) ? "selected" : ""}`}
                  onClick={() => {
                    onSelectRow(row);
                    setTab("bay");
                    scrollSavePanelIntoView();
                  }}
                >
                  {row}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {tab === "bay" && selectedRow && (
        <section className="panel selector-panel bay-panel" aria-label={`Select bay for row ${selectedRow}`}>
          <h3 className="panel-header">Select Bay for Row {selectedRow}</h3>
          <div
            className="bay-grid"
            style={{ "--bay-buttons-per-row": Number(bayButtonsPerRow) }}
          >
            {bays.map((bay) => (
              <button
                key={bay}
                type="button"
                className={`bay-button ${bay === Number(selectedBay) ? "selected" : ""}`}
                onClick={() => onSelectBay(bay)}
              >
                {bay}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="save-panel" ref={savePanelRef}>
        <div className="reassurance-banner">
          <div className="round-icon">
            <LocationOnIcon />
          </div>
          <div className="selected-location-copy">
            <div className="eyebrow">Selected Location</div>
            <div className="headline">{selectedLocationText}</div>
          </div>
        </div>

        {!saveDisabled && (
          <button
            ref={saveButtonRef}
            className="touch-button primary"
            type="button"
            onClick={onSave}
          >
            <SaveIcon /> SAVE LOCATION
          </button>
        )}
      </section>
    </section>
  );
}
